'use client';
import { useTranslation } from 'react-i18next';
import Image from "next/image";
import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { Icon } from "@iconify/react";

export function FloatingWidget() {
    const { t } = useTranslation();
    const translationKey = 'Contact.floatingWidget';

    // Refs for GSAP
    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const arrowsRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const container = containerRef.current;
        const button = buttonRef.current;

        if (!container || !button) return;

        // Entrance animation: Smooth slide-up from bottom
        gsap.fromTo(container,
            { y: 60, opacity: 0 },
            {
                y: 0,
                opacity: 1,
                duration: 1.2,
                ease: "power4.out",
                delay: 0.8,
                onComplete: () => {
                    // Use CSS animation for idle pulse instead of GSAP to free main thread
                    button.style.animation = "widgetPulse 2s ease-in-out infinite alternate";
                }
            }
        );

        return () => {
            gsap.killTweensOf(container);
        };
    }, []);

    const handleMouseEnter = () => {
        const button = buttonRef.current;
        const arrows = arrowsRef.current;
        if (!button) return;

        // Pause CSS idle pulse during hover
        button.style.animationPlayState = "paused";
        button.style.transform = "scale(1.04) translateY(-3px)";

        if (arrows) {
            arrows.style.transform = "translateX(3px)";
            arrows.style.opacity = "0.9";
        }
    };

    const handleMouseLeave = () => {
        const button = buttonRef.current;
        const arrows = arrowsRef.current;
        if (!button) return;

        // Resume CSS idle pulse
        button.style.transform = "";
        button.style.animationPlayState = "running";

        if (arrows) {
            arrows.style.transform = "";
            arrows.style.opacity = "0.4";
        }
    };

    const handleMouseDown = () => {
        const button = buttonRef.current;
        if (!button) return;
        button.style.transform = "scale(0.97)";
    };

    const handleMouseUp = () => {
        const button = buttonRef.current;
        if (!button) return;
        button.style.transform = "scale(1.04) translateY(-3px)";
    };

    return (
        <div
            ref={containerRef}
            className="fixed bottom-6 right-6 md:bottom-10 md:right-10 z-50 opacity-0"
        >
            <button
                ref={buttonRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                data-cal-namespace="30min"
                data-cal-link="ellis-caicedo-hfdo7f/30min"
                data-cal-config='{"layout":"month_view","useSlotsViewOnSmallScreen":"true"}'
                className="flex items-center text-left bg-white/95 dark:bg-slate-950/80 backdrop-blur-md rounded-[50px] shadow-[0_4px_30px_rgba(0,0,0,0.1)] dark:shadow-[0_4px_30px_rgba(0,0,10,0.4)] border border-slate-200/60 dark:border-slate-800/60 hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-300"
                style={{
                    padding: '8px 20px 8px 8px',
                    gap: '12px',
                    WebkitTapHighlightColor: "transparent"
                }}
            >
                <div className="w-[48px] h-[48px] rounded-full overflow-hidden border-2 border-white dark:border-slate-900 shadow-sm shrink-0">
                    <Image
                        src="/images/ellisv.webp"
                        alt="Profile"
                        className="w-full h-full object-cover"
                        width={48} // Add width and height for Next.js Image component
                        height={48}
                    />
                </div>

                <div className="flex flex-col text-left group-hover:px-1 transition-all duration-300">
                    <span className="text-sm font-semibold text-[#181A1D] dark:text-white leading-tight">
                        {t('Contact.bookCall')}
                    </span>
                    <p className="text-[12px] text-slate-900 dark:text-slate-200 font-medium leading-tight mt-0.5">
                        {t('Contact.getStarted')}
                    </p>
                </div>

                <div ref={arrowsRef} className="flex items-center opacity-40 ml-1 text-slate-400 dark:text-slate-500">
                    <Icon icon="tabler:chevron-right" className="w-[18px] h-[18px]" />
                </div>
            </button>
        </div>
    );
}
